/**
 * Apply Landing Page
 * Simple, CTA-focused funding path selection
 */

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { 
  ArrowRight, 
  Building2, 
  CreditCard, 
  DollarSign,
  Phone
} from "lucide-react";

const fundingOptions = [
  {
    id: "business-funding",
    title: "Business Funding",
    description: "SBA loans, lines of credit, equipment financing & more",
    icon: DollarSign,
    color: "#e8821c",
    link: "https://app.trulli.ai/marketplace/legacytriangle",
    external: true
  },
  {
    id: "business-credit",
    title: "Business Credit Building",
    description: "Build your EIN-based business credit profile",
    icon: CreditCard,
    color: "#48d480",
    link: "/business-credit-builder",
    external: false
  },
  {
    id: "commercial-re",
    title: "Commercial Real Estate",
    description: "Fix & flip, DSCR, construction & portfolio loans",
    icon: Building2,
    color: "#0f2d52",
    link: "https://relip-facebook-ads.vercel.app/broker/recHJ1mw7UyeDoVaT",
    external: true
  }
];

function FundingOption({ option }) {
  const Icon = option.icon;
  
  const CardWrapper = option.external ? 'a' : Link;
  const cardProps = option.external 
    ? { href: option.link, target: "_blank", rel: "noopener noreferrer" }
    : { to: option.link };

  return (
    <CardWrapper 
      {...cardProps}
      className="group flex items-center gap-4 bg-white rounded-xl p-5 shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-gray-200"
      style={{ '--hover-color': option.color }}
    >
      <div 
        className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
        style={{ backgroundColor: `${option.color}15` }}
      >
        <Icon size={26} style={{ color: option.color }} />
      </div>
      
      <div className="flex-1 min-w-0">
        <h3 
          style={{ fontFamily: "'Montserrat', sans-serif" }}
          className="text-lg font-bold text-[#0f2d52] mb-1"
        >
          {option.title}
        </h3>
        <p 
          style={{ fontFamily: "'Inter', sans-serif" }}
          className="text-gray-500 text-sm"
        >
          {option.description}
        </p>
      </div>
      
      <div 
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110"
        style={{ backgroundColor: option.color }}
      >
        <ArrowRight size={18} className="text-white" />
      </div>
    </CardWrapper>
  );
}

export default function ApplyLanding() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0f2d52]" data-testid="apply-landing-page">
      <Navbar />

      {/* Main Section */}
      <section className="min-h-[calc(100vh-80px)] flex items-center justify-center py-20 px-4">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="text-center mb-10">
            <h1
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-black text-white leading-tight mb-4"
            >
              What Are You
              <span className="text-[#48d480]"> Looking For?</span>
            </h1>
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-white/60 text-base"
            >
              Select an option to get started
            </p>
          </div>

          {/* Options */}
          <div className="space-y-4 mb-10">
            {fundingOptions.map((option) => (
              <FundingOption key={option.id} option={option} />
            ))}
          </div>

          {/* Help CTA */}
          <div className="text-center">
            <p className="text-white/50 text-sm mb-3">Not sure? We can help.</p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-[#48d480] font-semibold hover:underline"
            >
              <Phone size={16} />
              Schedule a Free Consultation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
